import { type SvgProps } from '@manomano-internal/tamagoshi-icons/esm/SvgOptimizer/Svg';
import classnames from 'classnames';
import { type FunctionComponent, type ReactElement } from 'react';

import { Alert as Danger } from '@/core/tamagoshi/icons/Alert';
import { CheckCircle as Success } from '@/core/tamagoshi/icons/CheckCircle';
import { Info } from '@/core/tamagoshi/icons/Info';
import { Warning } from '@/core/tamagoshi/icons/Warning';

import styles from './FormFieldMessage.module.scss';

const ICONS: Record<MessageType, FunctionComponent<SvgProps> | null> = {
  danger: Danger,
  info: Info,
  none: null,
  success: Success,
  warning: Warning,
};

export type MessageType = 'danger' | 'info' | 'none' | 'success' | 'warning';

export interface FormFieldMessageProps {
  'data-testid'?: string;
  message?: string | ReactElement;
  messageType?: MessageType;
  /** @deprecated Use data-testid instead. */
  testId?: string;
}

export const FormFieldMessage: FunctionComponent<FormFieldMessageProps> = ({
  'data-testid': dataTestId,
  message,
  testId: oldTestId,
  messageType = 'danger',
}) => {
  if (!message) {
    return null;
  }
  const testId = dataTestId ?? oldTestId;
  const MessageIcon = ICONS[messageType];
  return (
    <p
      aria-live="polite"
      className={classnames(styles.message, {
        [styles.danger]: messageType === 'danger',
        [styles.warning]: messageType === 'warning',
        [styles.info]: messageType === 'info',
        [styles.success]: messageType === 'success',
      })}
      data-testid={testId && `${testId}-message`}
      data-testvalue={testId && messageType}
    >
      {MessageIcon && <MessageIcon className={styles.icon} />}
      <span>{message}</span>
    </p>
  );
};

FormFieldMessage.displayName = 'FormFieldMessage';
